import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, MessageSquare, Calendar, Bell, CreditCard, Truck, ArrowRight, AlertTriangle } from "lucide-react";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { api } from "../../lib/api";

const EMPTY_STATS = {
  nouvellesDemandes: 0, modifications: 0, reservationsAVenir: 0, messages: 0,
  rendezVous: 0, alertesStock: 0, soldesARecevoir: 0, livraisonsSemaine: 0,
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [reservations, setReservations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats((prev) => ({
      ...prev,
      nouvellesDemandes: r.data.nouvelles_demandes,
      modifications: r.data.modifications_demandees,
      reservationsAVenir: r.data.confirmees,
    }))).catch(() => {});
    api.get("/admin/reservations").then((r) => {
      if (r.data.length) {
        setReservations(r.data.map((d) => ({
          ref: d.ref, id: d.id,
          client: d.customer ? `${d.customer.first_name} ${d.customer.last_name}` : "—",
          event: d.event?.type || "—", date: d.event?.start_date || "—",
          total: d.totals?.total_ttc || 0, status: d.status,
        })));
      }
    }).catch(() => {});
    api.get("/admin/notifications").then((r) => {
      if (r.data.length) {
        // v1.6 : seules les alertes ACTIVES (non résolues, non archivées) restent visibles ici
        const actives = r.data.filter((n) => !n.resolved && !n.archived);
        setAlerts(actives);
        setStats((prev) => ({ ...prev, alertesStock: actives.length }));
      }
    }).catch(() => {});
    api.get("/admin/logistics/overview").then((r) => {
      setStats((prev) => ({ ...prev, livraisonsSemaine: r.data.items.filter((i) => !["retourne", "controle"].includes(i.status)).length }));
    }).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B01 — Tableau de bord</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Bonjour, voici vos priorités</h1>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="admin-priorities">
        <StatCard icon={ShoppingBag} label="Nouvelles demandes" value={stats.nouvellesDemandes} hint="À valider sous 24 h" tone="gold" />
        <StatCard icon={MessageSquare} label="Messages non lus" value={stats.messages} />
        <StatCard icon={Calendar} label="Rendez-vous à venir" value={stats.rendezVous} />
        <StatCard icon={AlertTriangle} label="Alertes disponibilité" value={stats.alertesStock} tone="alert" />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="admin-kpis">
        <StatCard icon={ShoppingBag} label="Réservations à venir" value={stats.reservationsAVenir} />
        <StatCard icon={CreditCard} label="Soldes à recevoir" value={stats.soldesARecevoir} hint="Échéance J-7" />
        <StatCard icon={Truck} label="Livraisons / retraits" value={stats.livraisonsSemaine} hint="Cette semaine" />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <div className="card-ae xl:col-span-2" data-testid="admin-latest-requests">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="font-heading text-lg text-deepgreen">Dernières demandes</h2>
            <Link to="/admin/reservations" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-deepgreen hover:text-sage" data-testid="admin-see-all-reservations">
              Tout voir <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {reservations.length === 0 && (
              <li className="px-5 py-6 text-sm text-muted-foreground" data-testid="admin-dash-no-reservation">Aucune demande pour le moment.</li>
            )}
            {reservations.slice(0, 4).map((r) => (
              <li key={r.ref}>
                <Link to={`/admin/reservations/${r.id || r.ref}`} className="flex items-center justify-between gap-4 px-5 py-3.5 transition-colors hover:bg-cream/60" data-testid={`admin-dash-reservation-${r.ref}`}>
                  <div>
                    <p className="text-sm font-semibold text-deepgreen">{r.ref}</p>
                    <p className="text-xs text-muted-foreground">{r.client} — {r.event} — {r.date}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-ae" data-testid="admin-latest-alerts">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="font-heading text-lg text-deepgreen">Alertes récentes</h2>
            <Link to="/admin/notifications" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-deepgreen hover:text-sage" data-testid="admin-see-all-notifications">
              Centre <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {alerts.length === 0 && (
              <li className="px-5 py-6 text-sm text-muted-foreground" data-testid="admin-dash-no-alert">Aucune alerte en cours.</li>
            )}
            {alerts.slice(0, 5).map((n) => (
              <li key={n.id} className="flex items-start gap-3 px-5 py-3.5" data-testid={`admin-dash-alert-${n.id}`}>
                <Bell className={`mt-0.5 h-4 w-4 shrink-0 ${n.priority === "Urgent" ? "text-red-500" : n.priority === "Important" ? "text-gold" : "text-aeblue"}`} />
                <div>
                  <p className="text-sm text-foreground/85">{n.text}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{n.date || (n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
